import React, {useCallback, useEffect, useRef, useState} from 'react';
import {MdOutlineCancel, MdOutlineSupervisedUserCircle, MdPauseCircleOutline} from "react-icons/md";
import DefaultModal from "../../../../../components/Modal/DefaultModal";
import {useAppDispatch, useAppSelector} from "../../../../../helpers/redux";
import AssignKatapult from "./AssignKatapult";
import {RootState} from "../../../../../app/store";
import {
    cancelKatapult,
    getKatapultWithFilter,
    reset,
    waitingKatapult
} from "../processKatapult.reducer";
import {Link} from "react-router-dom";
import {DETAIL_TYPE_KATAPULT} from "features/worksheet/process/job/constants";
import {KatapultMainModel} from "../models";
import {InputStatusModel} from "../../job/models";
import {customStatusOption, customWarnOption} from "../../../../../helpers/alertLabel";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import KatapultChangeStatus from "./KatapultChangeStatus";
import {HttpClientError, RequestStatus} from "../../../../../http-client";
import {rowNumberRenderer} from "../../../../../components/DataGrid/agGridCustomProps";
import {checkRoleActionAccess} from "../../../../../routes/checkRoleAccess";
import {ActionEnum, MenuEnum} from "../../../../../routes/models";
import {UserCredential} from "../../../../login/models";
import AgDataGridServerPaging from "../../../../../components/DataGrid/AgDataGridServerPaging";
import {isArrayEmpty} from "../../../../../helpers/checkEmptiness";
import {getSortModel, getSortType} from "../../../../approval/constants";
import {dateSetTimeToNow} from "../../../../../helpers/convert";
import {katapultSortOption} from "../constants";

const KatapultTable = () => {
    const gridRef = useRef<any>(null);
    const dispatch = useAppDispatch();
    const MySwal = withReactContent(Swal);

    const [showModal, setShowModal] = useState<boolean>(false);
    const [selectedJobId, setSelectedJobId] = useState<string | undefined>(undefined);
    const [showChangeStatusModal, setShowChangeStatusModal] = useState<boolean>(false);
    const [action, setAction] = useState<string>('');
    const [jobId, setJobId] = useState<string>('');

    const user = useAppSelector(
        (state: RootState) => state.user.userAuth ?? {} as UserCredential
    );
    const status = useAppSelector((state: RootState) => state.processKatapult.status);
    const statusPole = useAppSelector((state: RootState) => state.pole.status);
    const actionStatus = useAppSelector((state: RootState) => state.processKatapult.actionStatus);
    const error = useAppSelector(
        (state: RootState) => state.job.error ?? ({} as HttpClientError)
    );

    useEffect(() => {
        if (actionStatus !== undefined && actionStatus !== RequestStatus.pending) {
            MySwal.fire(customStatusOption(actionStatus === RequestStatus.success, error.data?.message, 'status has been changed!', 'failed to change status')).then(() => {
                dispatch(reset());
                setShowChangeStatusModal(false);
                refreshCache(undefined);
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actionStatus, error]);

    useEffect(() => {
        if (status === RequestStatus.success) {
            refreshCache(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    useEffect(() => {
        if (statusPole === RequestStatus.success) {
            refreshCache(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusPole]);

    const toggleModal = () => {
        setShowModal((prevState) => !prevState);
        setSelectedJobId(undefined);
    };

    const openModal = (data: KatapultMainModel) => {
        setShowModal(true);
        setSelectedJobId(data.id);
    };

    const openChangeStatusModal = (action: string, id: string) => {
        setAction(action);
        setShowChangeStatusModal(true);
        setJobId(id);
    };

    const toggleChangeStatusModal = () => {
        setShowChangeStatusModal((prevState) => !prevState);
        setAction('');
        setJobId('');
    };

    const onStatusClicked = (id: string, action: string, data?: InputStatusModel) => {
        MySwal.fire(
            customWarnOption(
                'Change Status',
                'Are you sure you want to change the status'
            )
        ).then((result) => {
            if (result.isConfirmed) {
                switch (action) {
                    case 'waiting':
                        dispatch(waitingKatapult({
                            job_id: id,
                            waiting_notes: data?.notes ?? '',
                            waiting_subject_id: null,
                            waiting_by_user: user.user_id
                        }));
                        break;
                    case 'cancel':
                        dispatch(cancelKatapult({
                            job_id: id,
                            cancel_notes: data?.notes ?? '',
                            waiting_by_user: user.user_id
                        }));
                        break;
                    default:
                        break;
                }
            }
        });
    };

    const JobNumberLinkCellRender = (props: any) => (
        <Link
            to={`/worksheet/katapult/katapult-detail/${props.data?.id}`}
            className="underline text-blue-700"
        >
            {props.data?.number_job}
        </Link>
    );

    const ActionCellRender = (props: any) => {

        if (props.data === undefined) {
            return null;
        }

        return (
            <div className="flex align-middle h-[100%]">
                {
                    checkRoleActionAccess(user?.roleName, MenuEnum.WorksheetKatapult, ActionEnum.ActionAssign) &&
                    <button onClick={() => openModal(props.data)}>
                        <MdOutlineSupervisedUserCircle className="text-2xl text-primary-color"/>
                    </button>
                }
                {
                    checkRoleActionAccess(user?.roleName, MenuEnum.WorksheetKatapult, ActionEnum.ActionWaiting) &&
                    <button onClick={() => openChangeStatusModal('waiting', props.data?.id)}>
                        <MdPauseCircleOutline
                            className="text-2xl text-primary-color"
                        />
                    </button>
                }
                {
                    checkRoleActionAccess(user?.roleName, MenuEnum.WorksheetKatapult, ActionEnum.ActionCancel) &&
                    <button onClick={() => openChangeStatusModal('cancel', props.data?.id)}>
                        <MdOutlineCancel
                            className="text-2xl text-primary-color"
                        />
                    </button>
                }
            </div>
        )
    }

    const [columnDefs, _setColumnDefs] = useState([
        {
            headerName: '#',
            field: 'rowNumber',
            minWidth: 50,
            cellRenderer: rowNumberRenderer, // Use the custom cell renderer
        },
        {
            field: 'project_name', headerName: 'Project',
            filter: 'agTextColumnFilter', filterParams: {
                filterOptions: ['contains'],
                maxNumConditions: 1,
            }
        },
        {
            field: 'department_name', headerName: 'Department',
            filter: 'agTextColumnFilter', filterParams: {
                filterOptions: ['contains'],
                maxNumConditions: 1,
            }
        },
        {
            field: 'department_owner_name', headerName: 'Department Owner',
            filter: false, sortable: false
        },
        {field: 'district_name', headerName: 'District', filter: false, sortable: false},
        {field: 'type_name', headerName: 'Type', filter: false, sortable: false},
        {
            field: 'kind_name', headerName: 'KoJ',
            filter: 'agTextColumnFilter', filterParams: {
                filterOptions: ['contains'],
                maxNumConditions: 1,
            }
        },
        {
            field: 'ts_created',
            headerName: 'Request Date',
            cellDataType: 'date',
            filter: 'agDateColumnFilter',
            filterParams: {
                filterOptions: ['inRange'],
                maxNumConditions: 1,
            },
        },
        {
            field: 'number_id', headerName: 'ID Number',
            filter: 'agTextColumnFilter', filterParams: {
                filterOptions: ['contains'],
                maxNumConditions: 1,
            }
        },
        {
            headerName: 'Job Number',
            field: 'number_job',
            suppressCellFlash: true,
            cellRenderer: JobNumberLinkCellRender,
            filter: 'agTextColumnFilter', filterParams: {
                filterOptions: ['contains'],
                maxNumConditions: 1,
            }
        },
        {
            field: 'other_number', headerName: 'Other Number',
            filter: 'agTextColumnFilter', filterParams: {
                filterOptions: ['contains'],
                maxNumConditions: 1,
            }
        },
        {
            field: 'status_name', headerName: 'Status',
            filter: 'agTextColumnFilter', filterParams: {
                filterOptions: ['contains'],
                maxNumConditions: 1,
            }
        },
        {
            field: 'date_due',
            headerName: 'Due Date',
            cellDataType: 'date',
            filter: 'agDateColumnFilter',
            filterParams: {
                filterOptions: ['inRange'],
                maxNumConditions: 1,
            },
        },
        {
            headerName: 'Assign Percentage',
            field: 'percent_cs',
            suppressCellFlash: true,
            cellRenderer: (props: any) => <span>{props.data?.percent_cs.toFixed(0) + '%'}</span>,
            filter: false, sortable: false
        },
        {
            field: 'date_assigned_katapult',
            headerName: 'Date Assigned',
            cellDataType: 'date',
            filter: 'agDateColumnFilter',
            filterParams: {
                filterOptions: ['inRange'],
                maxNumConditions: 1,
            },
        },
        {
            field: 'pole_total', headerName: 'Request Pole',
            filter: 'agTextColumnFilter', filterParams: {
                filterOptions: ['contains'],
                maxNumConditions: 1,
            }
        },
        {
            headerName: 'Actions',
            field: 'actions',
            pinned: 'right',
            suppressCellFlash: true,
            minWidth: 220,
            cellRenderer: ActionCellRender,
            cellStyle: () => ({
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
            })
        },
    ]);

    const getServerSideDataSource = () => {
        return {
            getRows: (params: any) => {
                const sortModel = isArrayEmpty(params.request.sortModel) ? undefined : getSortModel(katapultSortOption, params.request.sortModel[0].colId) ?? undefined;
                const sortType = isArrayEmpty(params.request.sortModel) ? undefined : getSortType(params.request.sortModel[0].sort) ?? undefined;
                const {dateFrom: startDate, dateTo: endDate} = params.request.filterModel?.ts_created || {};
                const {dateFrom: startDueDate, dateTo: endDueDate} = params.request.filterModel?.date_due || {};
                const {
                    dateFrom: startDateAssign,
                    dateTo: endDateAssign
                } = params.request.filterModel?.date_assigned_katapult || {};

                dispatch(getKatapultWithFilter(
                    {
                        skip: params.request.startRow,
                        take: params.api.paginationGetPageSize(),
                        project_name: params.request.filterModel?.project_name?.filter,
                        department_name: params.request.filterModel?.department_name?.filter,
                        kind_job: params.request.filterModel?.kind_name?.filter,
                        startRequestDate: startDate !== undefined ? dateSetTimeToNow(startDate) : undefined,
                        endRequestDate: endDate !== undefined ? dateSetTimeToNow(endDate) : undefined,
                        number_id: params.request.filterModel?.number_id?.filter,
                        number_job: params.request.filterModel?.number_job?.filter,
                        other_number: params.request.filterModel?.other_number?.filter,
                        status: params.request.filterModel?.status_name?.filter,
                        startDueDate: startDueDate !== undefined ? dateSetTimeToNow(startDueDate) : undefined,
                        endDueDate: endDueDate !== undefined ? dateSetTimeToNow(endDueDate) : undefined,
                        startDateAssign: startDateAssign !== undefined ? dateSetTimeToNow(startDateAssign) : undefined,
                        endDateAssign: endDateAssign !== undefined ? dateSetTimeToNow(endDateAssign) : undefined,
                        req_pole: params.request.filterModel?.pole_total?.filter,
                        column: sortModel !== undefined ? parseInt(sortModel ?? "0") : undefined,
                        sort: sortModel !== undefined ? sortType : undefined
                    }
                )).then((res: any) => {
                    params.success({
                        rowData: res.payload?.data,
                        rowCount: res.payload.rows,
                    });
                }).catch((_err: any) => {
                    params.fail();
                })
            }
        }
    }

    const refreshCache = useCallback((route: any) => {
        const purge = !!document?.querySelector("#purge" as any)?.checked;
        gridRef.current.api.refreshServerSide({route: route, purge: purge});
    }, []);

    return (
        <div>
            <div className="main-data-grid mt-2">
                <div>
                    {/*<FormLoader isLoading={isLoading} label={"Fetching Data"}>*/}
                    {/*    <AgDataGridStatic ref={gridRef} columnDefs={columnDefs} rowData={katapultList} supportSaveTableState*/}
                    {/*                      tableStateName={'worksheet-katapult'}/>*/}
                    {/*</FormLoader>*/}
                    <AgDataGridServerPaging ref={gridRef} columnDefs={columnDefs} getServerSideDataSource={getServerSideDataSource} supportSaveTableState tableStateName={'worksheet-katapult'}
                                            defaultPageSize={20}
                    />
                </div>
                <DefaultModal
                    title="Assign"
                    show={showModal}
                    onClose={toggleModal}
                    size={'7xl'}
                    maxHeight="full"
                    body={<AssignKatapult id={selectedJobId} onHide={toggleModal}/>}
                />
                <DefaultModal
                    title="Change Status"
                    show={showChangeStatusModal}
                    onClose={toggleChangeStatusModal}
                    body={<KatapultChangeStatus action={action} jobId={jobId} onSubmit={onStatusClicked}/>}
                />
            </div>
        </div>
    );
};

export default KatapultTable;
